import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject } from '@angular/core';
import { AccountComplianceDto, ComplianceControllerService, OrderComplianceDto } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { SelectionModel } from '@shared/components/multi-select-with-exclude/multi-select-with-exclude.component';
import { DateTimeService } from '@shared/datetime/datetime.service';
import { SupportedTimezone } from '@shared/datetime/datetime.utils';
import { toApiTimezone } from '@shared/pipes/translate-timezone.pipe';
import { DownloadStateService } from '@shared/services/download-state.service';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { withUrlFilter } from '@shared/store/url/with-url-filter';
import { SortDirection } from '@shared/types/filter';
import { toArray } from '@shared/utils/api';
import { today, tomorrow } from '@shared/utils/datetime';
import { asUndefinedForEmptyString, toSelectionModel } from '@shared/utils/url-params';
import { Params } from '@angular/router';
import { getDateTimeAsString, isDefined } from '@stillmandigital/common';
import { filter, pipe, switchMap } from 'rxjs';
import { SharedComplianceStore } from '../shared-compliance.store';

export const COMPLIANCE_CLIENT_REPORT_DOWNLOAD_KEY = 'compliance-client-report-download';
export const COMPLIANCE_CLIENT_REPORT_ROUTE_ID = 'complianceClientReportRouteId';

const PAGE_SIZE = 100;

export interface ComplianceClientReportFilter {
  account?: AccountComplianceDto;
  pair?: SelectionModel;
  jurisdiction?: string;
  fromTime?: string;
  toTime?: string;
  sortDirection?: SortDirection;
  clOrdId?: string;
  skipCount?: number;
}

interface ComplianceClientReportState {
  orders: OrderComplianceDto[];
  pageFilter: ComplianceClientReportFilter | undefined;
  dataInProgress: boolean;
}

const initialState: ComplianceClientReportState = {
  orders: [],
  dataInProgress: false,
  pageFilter: {
    fromTime: getDateTimeAsString(today(), '00:00:00'),
    toTime: getDateTimeAsString(tomorrow(), '00:00:00'),
    sortDirection: 'DESC' as SortDirection,
  },
};

export const ComplianceClientReportStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    _complianceService: inject(ComplianceControllerService),
    _sharedComplianceStore: inject(SharedComplianceStore),
    _dateTimeService: inject(DateTimeService),
    _notificationService: inject(NotificationService),
    _downloadStateService: inject(DownloadStateService),
  })),
  withMethods(store => {
    const _pageFilterToUrlParams = (pageFilter: ComplianceClientReportFilter): Params => ({
      account: pageFilter.account?.id,
      jurisdiction: pageFilter.jurisdiction,
      pairInclude: pageFilter.pair?.include,
      pairExclude: pageFilter.pair?.exclude,
      fromTime: pageFilter.fromTime,
      toTime: pageFilter.toTime,
      sortDirection: pageFilter.sortDirection,
      clOrdId: asUndefinedForEmptyString(pageFilter.clOrdId ?? ''),
    });

    const _urlParamsToPageFilter = (params: Params): ComplianceClientReportFilter => ({
      account: store._sharedComplianceStore.accountsMap()[params['account']],
      pair:
        params['pairInclude'] || params['pairExclude']
          ? toSelectionModel(params['pairInclude'], params['pairExclude'])
          : undefined,
      jurisdiction: params['jurisdiction'],
      fromTime: params['fromTime'],
      toTime: params['toTime'],
      sortDirection: (params['sortDirection'] as SortDirection) ?? 'DESC',
      clOrdId: params['clOrdId'],
    });

    return { _pageFilterToUrlParams, _urlParamsToPageFilter };
  }),
  withComputed(store => ({
    nextPossible: computed(() => store.orders().length >= PAGE_SIZE),
    prevPossible: computed(() => (store.pageFilter()?.skipCount ?? 0) > 0),
    pageNumber: computed(() => (store.pageFilter()?.skipCount ?? 0) / PAGE_SIZE + 1),
  })),
  withFeature(() => withUrlFilter<ComplianceClientReportFilter>(COMPLIANCE_CLIENT_REPORT_ROUTE_ID)),
  withMethods(store => {
    const updatePageFilter = (pageFilter: Partial<ComplianceClientReportFilter>) => {
      patchState(store, { pageFilter: { ...(store.pageFilter() ?? {}), ...pageFilter } });
    };

    const nextPage = () => {
      const current = store.pageFilter();
      if (!current) {
        return;
      }
      updatePageFilter({ skipCount: (current.skipCount ?? 0) + PAGE_SIZE });
    };

    const prevPage = () => {
      const current = store.pageFilter();
      if (!current) {
        return;
      }
      updatePageFilter({ skipCount: Math.max((current.skipCount ?? 0) - PAGE_SIZE, 0) });
    };

    const loadOrders = rxMethod<ComplianceClientReportFilter>(
      pipe(
        filter(isDefined),
        switchMap(pageFilter =>
          store._complianceService
            .getOrders1({
              accountId: toArray(pageFilter.account?.id),
              pairId: pageFilter.pair?.include,
              excludePairIds: pageFilter.pair?.exclude,
              jurisdiction: pageFilter.jurisdiction,
              fromTime: pageFilter.fromTime && store._dateTimeService.toAPIDate(pageFilter.fromTime),
              toTime: pageFilter.toTime && store._dateTimeService.toAPIDate(pageFilter.toTime),
              sortDirection: pageFilter.sortDirection,
              skipCount: pageFilter.skipCount,
              clOrdId: pageFilter.clOrdId,
            })
            .pipe(
              tapResponse({
                next: orders => patchState(store, { orders, dataInProgress: false }),
                error: (error: HttpErrorResponse) => {
                  patchState(store, { dataInProgress: false });
                  store._notificationService.showErrorNotification(`The client report couldn't be loaded`, error);
                },
              }),
            ),
        ),
      ),
    );

    const refresh = () => {
      const pageFilter = store.pageFilter();
      if (!pageFilter) {
        return;
      }
      patchState(store, { dataInProgress: true });
      loadOrders(pageFilter);
    };

    const downloadClientReport = rxMethod<{ customZone?: SupportedTimezone }>(
      pipe(
        switchMap(({ customZone }) => {
          const pageFilter = store.pageFilter();
          store._downloadStateService.start(COMPLIANCE_CLIENT_REPORT_DOWNLOAD_KEY);

          return store._complianceService
            .clientReport1({
              accountId: pageFilter?.account?.id,
              pairId: pageFilter?.pair?.include,
              excludePairIds: pageFilter?.pair?.exclude,
              jurisdiction: pageFilter?.jurisdiction,
              fromTime: pageFilter?.fromTime ? store._dateTimeService.toAPIDate(pageFilter.fromTime) : undefined,
              toTime: pageFilter?.toTime ? store._dateTimeService.toAPIDate(pageFilter.toTime) : undefined,
              customZone: customZone ? toApiTimezone(customZone) : undefined,
            })
            .pipe(
              tapResponse({
                next: (response: { id: number }) => {
                  store._sharedComplianceStore.addPendingDownload({
                    id: response.id,
                    fileName: 'compliance-client-report.csv',
                    downloadKey: COMPLIANCE_CLIENT_REPORT_DOWNLOAD_KEY,
                  });
                },
                error: (error: HttpErrorResponse) => {
                  store._downloadStateService.error(COMPLIANCE_CLIENT_REPORT_DOWNLOAD_KEY, error);
                  store._notificationService.showErrorNotification(`The client report couldn't be downloaded`, error);
                },
              }),
            );
        }),
      ),
    );

    return {
      updatePageFilter,
      nextPage,
      prevPage,
      loadOrders,
      refresh,
      downloadClientReport,
    };
  }),
  withHooks({
    onInit(store) {
      effect(() => {
        const pageFilter = store.pageFilter();
        if (isDefined(pageFilter)) {
          patchState(store, { dataInProgress: true });
          store.loadOrders(pageFilter);
        }
      });
    },
    onDestroy(store) {
      store._downloadStateService.clear(COMPLIANCE_CLIENT_REPORT_DOWNLOAD_KEY);
    },
  }),
);
